import React, { useEffect, useRef, useState } from "react";
import { initHighchartsModules } from "../../../../../utils/initHighchartsModules";
import {
  Button,
  Card,
  Col,
  Dropdown,
  DropdownButton,
  Row,
} from "react-bootstrap";
import {
  fetchInvoiceDistributionByRoleData,
  fetchInvoiceTrendsData,
} from "../../../../../services/BillingDashboardAPI";
import AccordionComponent from "../../../../../common/Accordion";
import HighchartsReact from "highcharts-react-official";
import Highcharts from "highcharts";
import YearDropdown from "../../../../../common/YearDropdown";

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const MONTH_MAP: Record<string, string> = {
  Jan: "01",
  Feb: "02",
  Mar: "03",
  Apr: "04",
  May: "05",
  Jun: "06",
  Jul: "07",
  Aug: "08",
  Sep: "09",
  Oct: "10",
  Nov: "11",
  Dec: "12",
};

interface InvoiceTrendNDistributionProps {
  openDropdown: string | null;
  setOpenDropdown: React.Dispatch<React.SetStateAction<string | null>>;
  filters: {
    teamId: string | number | null;
    clientId: string | number | null;
    currencyType: string;
    yearMonth: Date | null;
  };
}

const InvoiceTrendNDistribution: React.FC<InvoiceTrendNDistributionProps> = ({
  openDropdown,
  setOpenDropdown,
  filters,
}) => {
  const [selectedYear, setSelectedYear] = useState<number>(
    new Date().getFullYear(),
  );
  const [selectedYearMonth, setSelectedYearMonth] = useState<string>(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  });
  const [trendData, setTrendData] = useState<any | null>(null);
  const [chartOptions, setChartOptions] = useState<Highcharts.Options>({});

  const [distributionData, setDistributionData] = useState<any | null>(null);

  useEffect(() => {
    initHighchartsModules();
  }, []);

  const formatYearMonth = (date: Date | null) => {
    if (!date) return null;
    return date.toISOString().slice(0, 7); // YYYY-MM
  };

  const fetchInvoiceTrendData = async () => {
    try {
      const payload = {
        teamId: filters.teamId,
        clientId: filters.clientId,
        currencyType: filters.currencyType,
        year: selectedYear,
      };

      const result = await fetchInvoiceTrendsData(payload);
      setTrendData(result?.result);
    } catch (error) {
      console.error("Failed to load case overview data", error);
    }
  };

  useEffect(() => {
    fetchInvoiceTrendData();
  }, [filters, selectedYear]);

  const parsedTrendData = trendData?.statusCounts
    ? JSON.parse(trendData.statusCounts)
    : [];

  const categories = MONTHS;

  const seriesData: number[] = parsedTrendData.map((item: any) =>
    Number(item.count || 0),
  );

  const prepareTrendChart = () => {
    setChartOptions({
      chart: {
        type: "column",
        height: 300,
      },

      title: { text: undefined },

      xAxis: {
        categories,
        labels: {
          style: { fontSize: "12px" },
        },
      },

      yAxis: {
        min: 0,
        title: { text: "" },
      },

      tooltip: {
        pointFormat: "<b>{point.y}</b>",
      },

      plotOptions: {
        column: {
          color: "#1f9d73",
          dataLabels: {
            enabled: true,
          },
          cursor: "pointer",
          point: {
            events: {
              click: function () {
                const monthName = this.category as string; // "Feb"
                const monthNumber = MONTH_MAP[monthName];

                const yearMonth = `${selectedYear}-${monthNumber}`;
                console.log("CLICKED:", yearMonth);
                // store in state
                setSelectedYearMonth(yearMonth);
              },
            },
          },
        },
      },

      exporting: {
        enabled: true,
        buttons: {
          contextButton: {
            enabled: true,
            symbol: "menu",
          },
        },
      },

      credits: { enabled: false },
      legend: { enabled: false },

      series: [
        {
          name: "Invoice Count",
          type: "column",
          data: seriesData,
        },
      ],
    });
  };

  useEffect(() => {
    if (trendData) {
      prepareTrendChart();
    }
  }, [trendData]);

  useEffect(() => {
    if (!selectedYearMonth) return;

    const month = selectedYearMonth.split("-")[1];
    const updatedYearMonth = `${selectedYear}-${month}`;

    setSelectedYearMonth(updatedYearMonth);
  }, [selectedYear]);

  const fetchInvoiceDistributionData = async () => {
    try {
      const payload = {
        teamId: filters.teamId,
        clientId: filters.clientId,
        currencyType: filters.currencyType,
        yearMonth: selectedYearMonth,
      };

      const result = await fetchInvoiceDistributionByRoleData(payload);
      setDistributionData(result?.result);
    } catch (error) {
      console.error("Failed to load case overview data", error);
    }
  };

  useEffect(() => {
    fetchInvoiceDistributionData();
  }, [filters, selectedYearMonth]);

  console.log(selectedYearMonth, "selectedYearMonth");

  const parsedDistributionData = distributionData?.statusCounts
    ? JSON.parse(distributionData.statusCounts)
    : [];

  const buildDonutChartOptions = (): Highcharts.Options => {
    const data = parsedDistributionData.map((item: any) => ({
      name: item.role,
      y: Number(item.count || 0),
      percentage: item.percentage,
    }));

    return {
      chart: {
        type: "pie",
        height: 300,
      },

      title: {
        text: undefined,
      },

      plotOptions: {
        pie: {
          innerSize: "60%", // donut
          dataLabels: {
            enabled: true,
            formatter: function (this: Highcharts.Point) {
              const point: any = this;

              return point.y > 0
                ? `${point.name} (${point.percentage}%)`
                : null;
            },
          },
        },
      },

      tooltip: {
        pointFormat: "<b>{point.name}</b>: {point.y} ({point.percentage}%)",
      },

      legend: {
        layout: "horizontal",
        align: "center",
        verticalAlign: "bottom",
      },

      series: [
        {
          type: "pie",
          name: "Distribution",
          data,
        },
      ],

      credits: { enabled: false },
    };
  };

  const [invoiceKey, setInvoiceKey] = useState<string | null>("0");

  const handleBodyAccordionSelect = (key: string | null) => {
    if (invoiceKey === key) {
      return;
    }
    setInvoiceKey(key || "0");
  };

  const summaryAccordion = (
    items: { title: JSX.Element; content: JSX.Element }[],
  ) => (
    <AccordionComponent
      items={items.map((item, index) => ({
        ...item,
      }))}
      activeKey={invoiceKey}
      onSelect={handleBodyAccordionSelect}
    />
  );

  const hasAnyData = seriesData.some((val) => val > 0);

  const hasDistributionData = parsedDistributionData.some(
    (item: any) => item.count > 0,
  );

  const formatYearMonthLabel = (value: string) => {
    if (!value) return "";

    const [year, month] = value.split("-");
    const date = new Date(Number(year), Number(month) - 1);

    const monthName = date.toLocaleString("default", { month: "short" });

    return `${monthName}-${year}`;
  };
  return (
    <>
      {summaryAccordion([
        {
          title: (
            <div className="d-flex align-items-center w-100 p-0">
              <span>Invoice Trend - Generated</span>

              <span style={{ marginLeft: "40%" }}>Distribution By Role</span>
            </div>
          ),
          content: (
            <>
              <Row className="g-3">
                {/* LEFT CHART */}
                <Col md={6}>
                  {/* Header row for dropdown */}
                  <div
                    className="d-flex justify-content-end align-items-center mb-2"
                    style={{ position: "relative" }}
                  >
                    <YearDropdown
                      title={selectedYear?.toString() || "Select Year"}
                      onSelect={(value) => setSelectedYear(value)}
                      show={openDropdown === "year"}
                      onToggle={(isOpen) =>
                        setOpenDropdown(isOpen ? "year" : null)
                      }
                    />
                  </div>

                  {/* Chart */}
                  {hasAnyData ? (
                    <HighchartsReact
                      highcharts={Highcharts}
                      options={chartOptions}
                    />
                  ) : (
                    <div className="nodata_wrapper">
                      <span className="material-symbols-outlined">
                        bar_chart_off
                      </span>
                      No Data Available
                    </div>
                  )}
                </Col>

                {/* RIGHT CHART */}
                <Col md={6}>
                  <>
                    {hasDistributionData ? (
                      <HighchartsReact
                        highcharts={Highcharts}
                        options={buildDonutChartOptions()}
                      />
                    ) : (
                      <div className="nodata_wrapper">
                        <span className="material-symbols-outlined">
                          bar_chart_off
                        </span>
                        No Data Available
                      </div>
                    )}

                    <div
                      style={{
                        textAlign: "center",
                        marginTop: "8px",
                        fontSize: "14px",
                        color: "#000000",
                        fontWeight: 500,
                      }}
                    >
                      {formatYearMonthLabel(selectedYearMonth)}
                    </div>
                  </>
                </Col>
              </Row>
            </>
          ),
        },
      ])}
    </>
  );
};

export default InvoiceTrendNDistribution;
